import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { analysisParameterError, fetchDeviceAnalysisParameters,
  saveAnalysisParameters } from '../api/analysisParameters';
import type { AnalysisParameters, DeviceAnalysisParameters, DeviceAnalysisParametersResponse } from '../types';
import { useConfirm } from '../context/ConfirmContext';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { analysisOverrideUnchanged, validateAnalysisParameters } from '../utils/analysisParameterValidation';
import Button from './ui/Button';
import ActionRow from './ui/ActionRow';
import { Label } from './pilot-ui/label';
import { Textarea } from './pilot-ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';
import AnalysisParametersTable from './AnalysisParametersTable';
import AnalysisRebuildStatusLine from './AnalysisRebuildStatusLine';
import { useAnalysisRebuildStatus } from '../hooks/useAnalysisRebuildStatus';

export default function AnalysisParametersSection({ deviceId }: { deviceId: number }) {
  const confirm = useConfirm();
  const id = useId();
  const [data, setData] = useState<DeviceAnalysisParametersResponse | null>(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState('{}');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const loadedGeneration = useRef(0);
  const loadedOverride = useRef<AnalysisParameters>({});
  const detailsGeneration = useRef(0);
  const enabled = data?.assignments.filter((assignment) => assignment.enabled) ?? [];
  const selected = enabled.find((assignment) => assignment.analysis_key === selectedKey);
  const debouncedDraft = useDebouncedValue(draft);
  const validation = validateAnalysisParameters(debouncedDraft, selected?.contract ?? null);

  const refreshCompleted = useCallback(async (generation: number, analysisKey: string) => {
    const refresh = ++detailsGeneration.current;
    try {
      const response = await fetchDeviceAnalysisParameters(deviceId);
      if (generation !== loadedGeneration.current || refresh !== detailsGeneration.current) return;
      const assignment = response.assignments.find((row) => row.analysis_key === analysisKey);
      setData(response);
      if (assignment) {
        const baseline = loadedOverride.current;
        loadedOverride.current = assignment.params_override;
        setDraft((current) => analysisOverrideUnchanged(current, baseline)
          ? JSON.stringify(assignment.params_override, null, 2) : current);
        return assignment.rebuild;
      }
    } catch (failure: unknown) {
      if (generation === loadedGeneration.current && refresh === detailsGeneration.current) {
        setError(analysisParameterError(failure));
      }
    }
  }, [deviceId]);

  const completed = useCallback(() => refreshCompleted(loadedGeneration.current, selectedKey), [refreshCompleted, selectedKey]);
  const { status, pollExpired, error: rebuildError, reset: resetRebuild, acceptCompletion } =
    useAnalysisRebuildStatus(deviceId, selectedKey, completed, () => setError(''));

  const showAssignment = useCallback((assignment?: DeviceAnalysisParameters) => {
    setSelectedKey(assignment?.analysis_key ?? '');
    loadedOverride.current = assignment?.params_override ?? {};
    setDraft(JSON.stringify(loadedOverride.current, null, 2));
    resetRebuild(assignment?.rebuild ?? null);
    setError('');
  }, [resetRebuild]);

  useEffect(() => {
    const generation = ++loadedGeneration.current;
    setLoading(true);
    setData(null);
    setSelectedKey('');
    setDraft('{}');
    setError('');
    setSaving(false);
    resetRebuild(null);
    fetchDeviceAnalysisParameters(deviceId).then((response) => {
      if (generation !== loadedGeneration.current) return;
      setData(response);
      const available = response.assignments.filter((assignment) => assignment.enabled);
      showAssignment(available.find((assignment) => assignment.is_default) ?? available[0]);
    }).catch((failure: unknown) => {
      if (generation === loadedGeneration.current) setError(analysisParameterError(failure));
    }).finally(() => { if (generation === loadedGeneration.current) setLoading(false); });
    return () => { loadedGeneration.current += 1; };
  }, [deviceId, showAssignment, resetRebuild]);

  const save = async () => {
    if (!selected || !validation.params || reason) return;
    const generation = loadedGeneration.current;
    const params = validation.params;
    const summary = Object.keys(params).length ? JSON.stringify(params, null, 2) : 'no overrides';
    const accepted = await confirm({ title: 'Save analysis parameters and rebuild', tone: 'danger', confirmLabel: 'Save and rebuild',
      message: `Save ${summary}. Rebuild from ${selected.period.start_date}. Every processed day of the current filter period and the learned model are deleted and rebuilt. The app's filter-life value can change.` });
    if (!accepted || generation !== loadedGeneration.current) return;
    setSaving(true);
    setError('');
    try {
      const result = await saveAnalysisParameters(selected.analysis_key, deviceId, params, true);
      if (generation !== loadedGeneration.current) return;
      setData((previous) => previous ? { ...previous, assignments: previous.assignments.map((assignment) =>
        assignment.analysis_key === selected.analysis_key ? { ...assignment, params_override: result.params_override,
          effective_params: result.effective_params, model: null, rebuild: result.rebuild ?? assignment.rebuild } : assignment) } : previous);
      detailsGeneration.current += 1;
      loadedOverride.current = result.params_override;
      setDraft(JSON.stringify(result.params_override, null, 2));
      resetRebuild(result.rebuild);
      if (result.rebuild?.status === 'completed') {
        const refreshed = await refreshCompleted(generation, selected.analysis_key);
        if (generation === loadedGeneration.current) acceptCompletion(result.rebuild, refreshed);
      }
    } catch (failure: unknown) {
      if (generation === loadedGeneration.current) setError(analysisParameterError(failure));
    } finally { if (generation === loadedGeneration.current) setSaving(false); }
  };

  const chooseMethod = (value: string | null) => {
    if (!value || value === selectedKey) return;
    loadedGeneration.current += 1;
    showAssignment(enabled.find((assignment) => assignment.analysis_key === value));
  };

  const reason = saving ? 'Saving analysis parameters…'
    : !selected?.contract ? "This method's parameters are not published by the analysis code yet."
      : !selected.period.replayable ? selected.period.reason ?? 'This filter period cannot be rebuilt.'
        : draft !== debouncedDraft ? 'Checking Override JSON…'
          : validation.issues.length ? validation.issues.map((issue) => issue.message).join(' ') : null;
  const inherited = data?.inherited;
  const shown = selected ?? inherited;
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-3 mt-6">
      <h2 id={`${id}-heading`} className="text-xl font-semibold">Analysis parameters</h2>
      {loading && <p role="status">Loading analysis parameters…</p>}
      {error && !data && <p role="alert">{error}</p>}
      {!loading && data && <>
        {enabled.length > 1 && <div>
          <Label htmlFor={`${id}-method`}>Analysis method</Label>
          <p id={`${id}-method-help`} className="text-sm text-muted-foreground">Choose the enabled method whose overrides you want to edit.</p>
          <Select value={selectedKey} onValueChange={chooseMethod}
            items={enabled.map((assignment) => ({ value: assignment.analysis_key, label: assignment.display_name }))}>
            <SelectTrigger id={`${id}-method`} aria-describedby={`${id}-method-help`} disabled={saving}><SelectValue /></SelectTrigger>
            <SelectContent listLabel="Analysis methods">{enabled.map((assignment) => <SelectItem key={assignment.analysis_key} value={assignment.analysis_key}>{assignment.display_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>}
        {shown && <p>{shown.display_name} · <code>{shown.handler_key}</code>{shown.contract && <> · {shown.contract.version}</>}</p>}
        {shown?.effective_params && <AnalysisParametersTable params={shown.effective_params} overrides={selected?.params_override} />}
        {!enabled.length ? <p>An assignment is required before parameters can be edited. Add or enable one in the Analysis Methods panel.</p>
          : !selected?.contract ? <p>This method&apos;s parameters are not published by the analysis code yet.</p>
            : <>
              <Label htmlFor={`${id}-override`}>Override JSON</Label>
              <p id={`${id}-help`} className="text-sm text-muted-foreground">Enter only the parameters to override as a JSON object. Other parameters keep their method defaults. Clear overrides prepares an empty draft; Save and rebuild applies it.</p>
              <Textarea id={`${id}-override`} value={draft} onChange={(event) => setDraft(event.target.value)} disabled={saving}
                aria-describedby={`${id}-help ${id}-validation`} aria-invalid={validation.issues.length > 0} className="font-mono" rows={8} />
              <ul id={`${id}-validation`} aria-live="polite">{validation.issues.map((issue, index) => <li key={`${issue.key}-${index}`}>{issue.message}</li>)}</ul>
              <ActionRow reason={reason} reasonId={`${id}-reason`}>
                <Button variant="secondary" title="Clear the override draft; save and rebuild to apply it" disabled={saving} onClick={() => setDraft('{}')}>Clear overrides</Button>
                <Button variant="primary" title="Save these overrides and rebuild the current filter period" disabled={!!reason}
                  aria-describedby={reason ? `${id}-reason` : undefined} onClick={save}>Save and rebuild</Button>
              </ActionRow>
            </>}
        {(error || rebuildError) && <p role="alert">{error || rebuildError}</p>}
        {saving ? <p role="status">Saving analysis parameters…</p>
          : <AnalysisRebuildStatusLine status={status} pollExpired={pollExpired} />}
        {selected?.model && <p>Model {selected.model.model_version}, saved through {selected.model.updated_through_date ?? 'no date'}.</p>}
      </>}
    </section>
  );
}
